// Pure helpers behind TrendChart's canvas. Kept out of the component so the
// bucketing and smoothing maths can be tested without a canvas.

/**
 * Buckets a time series into one slot per pixel column across the window.
 * Each occupied column keeps its own min, max, sum and count, so either a
 * spike envelope or a mean line can be drawn from the same pass.
 *
 * @param {{t: number, v: number}[]} points
 * @param {number} tStart window start, ms
 * @param {number} windowMs window length, ms
 * @param {number} width columns (CSS pixels)
 * @returns {({lo: number, hi: number, sum: number, n: number} | undefined)[]}
 */
export function bucketColumns(points, tStart, windowMs, width) {
  const columns = new Array(Math.ceil(width));

  for (const p of points) {
    const x = Math.floor(((p.t - tStart) / windowMs) * width);
    if (x < 0 || x >= columns.length) continue;

    const col = columns[x];
    if (!col) columns[x] = { lo: p.v, hi: p.v, sum: p.v, n: 1 };
    else {
      if (p.v < col.lo) col.lo = p.v;
      if (p.v > col.hi) col.hi = p.v;
      col.sum += p.v;
      col.n += 1;
    }
  }

  return columns;
}

/**
 * A sample-weighted moving average over the buckets: each occupied column's
 * value is the mean of every sample within `radius` columns either side
 * (sum of sums / sum of counts, so a busy column counts for more than a
 * sparse one -- it is a mean of samples, not a mean of means). Columns with no
 * samples stay empty, so the trace still shows where data stopped.
 *
 * @param {ReturnType<typeof bucketColumns>} columns
 * @param {number} radius in columns
 * @returns {(number | undefined)[]}
 */
export function smoothColumns(columns, radius) {
  const out = new Array(columns.length);

  for (let x = 0; x < columns.length; x += 1) {
    if (!columns[x]) continue;

    let sum = 0;
    let n = 0;
    const from = Math.max(0, x - radius);
    const to = Math.min(columns.length - 1, x + radius);
    for (let i = from; i <= to; i += 1) {
      const col = columns[i];
      if (!col) continue;
      sum += col.sum;
      n += col.n;
    }

    out[x] = sum / n;
  }

  return out;
}
